/**
 * Дедуп платежей банковского импорта.
 *
 * Правило то же, что у staging-дедупа (`makeStagingDedupKey` в
 * `royaltyService`): **строгий ключ, если у выписки есть номер документа;
 * слабый — только когда номера нет**. Слабый ключ (договор + дата + сумма +
 * ИНН + начало назначения) не содержит номера документа, поэтому применять
 * его ПОВЕРХ строгого нельзя.
 *
 * ⚠️ Он применялся именно поверх (`(primary && has(primary)) ||
 * has(fallback)`) и без назначения в ключе, из-за чего все платежи одного
 * договора за один день с одинаковой суммой схлопывались в один. Исправление
 * от 01.09.2026 до 15.09.2026 жило только в ветке и в `main` не попадало.
 * Номинальный счёт (Билгтон) платит по КАЖДОЙ клиентской транзакции, поэтому
 * одинаковые суммы в один день — норма: замер PROD 01.09.2026 — 790 настоящих
 * платежей на 305 949,30 ₽ (789 из них Билгтон, август 2026) не были созданы и остались
 * ждать в очереди `/royalties/allocation`. Что это не дубли, видно по данным:
 * у 76 августовских строк ДКК 882-ПП — 76 разных номеров документов и 76
 * разных номеров транзакций в назначении.
 *
 * Логика живёт здесь, а не в сервисе: сервис остаётся транспортом (выбрать
 * существующие платежи), а правило проверяется без сети
 * (`tests/bank-import-dedup.test.ts`).
 */

/** Поля, из которых строятся оба ключа. Годится и для строки файла, и для строки БД. */
export interface BankImportDedupRow {
  contract_id: number
  payment_date: string
  amount_rub: number
  payer_inn?: string | null
  payment_purpose?: string | null
  bank_document_number?: string | null
  bank_document_date?: string | null
}

function normalizeImportDate(value: string | null | undefined): string {
  return (value ?? '').trim().slice(0, 10)
}

function normalizeImportText(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase()
}

function normalizeImportAmount(value: number | null | undefined): string {
  const amount = Number(value ?? 0)
  return Number.isFinite(amount) ? amount.toFixed(2) : '0.00'
}

/**
 * Строгий ключ: договор + номер и дата документа + сумма + дата платежа.
 * `null`, если у выписки нет номера или даты документа — тогда работает слабый.
 */
export function makeBankImportPrimaryKey(row: BankImportDedupRow): string | null {
  const docNumber = normalizeImportText(row.bank_document_number)
  const docDate = normalizeImportDate(row.bank_document_date)
  if (!docNumber || !docDate) return null
  return [
    'STRONG',
    row.contract_id,
    docNumber,
    docDate,
    normalizeImportAmount(row.amount_rub),
    normalizeImportDate(row.payment_date),
  ].join('|')
}

/**
 * Слабый ключ для выписок без номера документа: договор + дата + сумма + ИНН
 * плательщика + начало назначения. Назначение в ключе обязательно — без него
 * две разные транзакции одного дня на одну сумму неразличимы (зеркало
 * `makeStagingFallbackKey`, который тоже несёт назначение).
 */
export function makeBankImportFallbackKey(row: BankImportDedupRow): string {
  return [
    'WEAK',
    row.contract_id,
    normalizeImportDate(row.payment_date),
    normalizeImportAmount(row.amount_rub),
    normalizeImportText(row.payer_inn),
    normalizeImportText(row.payment_purpose).slice(0, 200),
  ].join('|')
}

/** Даты платежей партии — по ним сужается выборка уже существующих платежей. */
export function bankImportPaymentDates(rows: Array<{ payment_date: string }>): string[] {
  return [...new Set(rows.map((row) => normalizeImportDate(row.payment_date)).filter(Boolean))]
}

/**
 * Делит партию на «к вставке» и «дубли» относительно уже существующих платежей.
 * Обе стороны сравниваются одним правилом; принятая строка сразу добавляет свои
 * ключи, поэтому дубли внутри самой партии тоже отсекаются.
 */
export function partitionBankImportDuplicates<T extends BankImportDedupRow>(
  existingRows: BankImportDedupRow[],
  rows: T[]
): { rowsToImport: T[]; duplicateRows: T[] } {
  const existingPrimaryKeys = new Set<string>()
  const existingFallbackKeys = new Set<string>()

  for (const row of existingRows) {
    const primary = makeBankImportPrimaryKey(row)
    if (primary) existingPrimaryKeys.add(primary)
    existingFallbackKeys.add(makeBankImportFallbackKey(row))
  }

  const rowsToImport: T[] = []
  const duplicateRows: T[] = []

  for (const row of rows) {
    const primary = makeBankImportPrimaryKey(row)
    const fallback = makeBankImportFallbackKey(row)
    // Строгий ключ вытесняет слабый, а не дополняется им: см. шапку файла.
    const isDuplicate = primary ? existingPrimaryKeys.has(primary) : existingFallbackKeys.has(fallback)

    if (isDuplicate) {
      duplicateRows.push(row)
      continue
    }

    rowsToImport.push(row)
    if (primary) existingPrimaryKeys.add(primary)
    existingFallbackKeys.add(fallback)
  }

  return { rowsToImport, duplicateRows }
}
